/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios, { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import UserContext from '../../../contexts/User'
import { UserContextType } from '../../../schemas/User'
import EntityContext from '../../../contexts/Entity'
import { EntityContextType } from '../../../schemas/Entity'
import UserService from '../../../services/User/index'
import { urls } from '../../../urls'
import { ServerError } from '../../../schemas/Error'
import Avatar from '@material-ui/core/Avatar'
import Button from '@material-ui/core/Button'
import CssBaseline from '@material-ui/core/CssBaseline'
import TextField from '@material-ui/core/TextField'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'
import Link from '@material-ui/core/Link'
import Grid from '@material-ui/core/Grid'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Container from '@material-ui/core/Container'
import CircularProgress from '@material-ui/core/CircularProgress'
import ForgotPasswordDialog from './ForgotPasswordDialog'

const CONTROL_USER_DOMAIN = (process.env.REACT_APP_CONTROL_USER_DOMAIN ?? 'crecerdmc.com').toLowerCase()
const CODE_VALIDITY_SECONDS = 5 * 60

// -------------- Styles --------------
const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: '#fafafa',
  },
  state: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  timer: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    fontVariantNumeric: 'tabular-nums',
  },
}))

// - in this part all the component texts are housed
const texts = {
  header: {
    title: 'Inicia sesión',
    logo: {
      path: '/images/logotipo-crecer-dmc.svg',
      alt: 'logotipo de crecer dmc'
    }
  },
  state: {
    field: {
      email: {
        name: 'email',
        label: 'Correo'
      },
      password: {
        name: 'password',
        label: 'Contraseña'
      },
      controlCode: {
        name: 'controlCode',
        label: 'Código de 6 caracteres'
      },
      rememberCheck: {
        value: 'remember',
        label: 'Recordarme'
      },
      signinButton: {
        title: 'Entrar'
      },
      controlSigninButton: {
        title: 'Autenticar en 2 pasos'
      }
    },
    modal: {
      title: 'Código de verificación',
      requestNewCode: 'Solicitar nuevo código',
      verify: 'Verificar',
      codeSent: 'Se envió un código a la casilla de control. Ingrésalo abajo.',
      blockedMessage: 'Bloqueado temporalmente. Intente nuevamente en 2 horas.'
    },
    footer: {
      forgotPasswordLink: 'Olvidaste tu contraseña?',
      createAccountLink: 'Aún no tienes un usuario?',
      copyright: 'CRECER DMC'
    }
  }
}

// ------------- Init state -----------
interface LoginForm {
  email: string
  password: string
}

const initState: LoginForm = {
  email: '',
  password: ''
}

function formatTimer (seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

function Copyright () {
  return (
    <Typography variant="body2" color="textSecondary" align="center">
      {'Copyright © '}
      <Link color="inherit" href="https://material-ui.com/">
        {texts.state.footer.copyright}
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  )
}

export default function Login () {
  const { setUserContext } = React.useContext(
    UserContext
  ) as UserContextType

  const { setEntityContext } = React.useContext(
    EntityContext
  ) as EntityContextType

  const navigate = useNavigate()
  const classes = useStyles()
  const [state, setState] = useState<LoginForm>(initState)
  const [controlModalOpen, setControlModalOpen] = useState(false)
  const [controlCode, setControlCode] = useState('')
  const [timerSeconds, setTimerSeconds] = useState(CODE_VALIDITY_SECONDS)
  const [controlLoading, setControlLoading] = useState(false)
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false)
  const [restoringSession, setRestoringSession] = useState(true)

  const emailDomain = state.email.trim().includes('@')
    ? (state.email.trim().split('@')[1]?.toLowerCase() ?? '')
    : ''
  const isControlUserFlow = emailDomain === CONTROL_USER_DOMAIN

  React.useEffect(() => {
    let cancelled = false

    async function checkExistingSession () {
      const userService = new UserService()
      const session = await userService.tryRestoreSession()
      if (cancelled) {
        return
      }
      if (session) {
        setUserContext(session.user)
        setEntityContext(session.entity)
        navigate(`/${urls.app.index}/${urls.app.main.electronicBill.form}`, { replace: true })
        return
      }
      setRestoringSession(false)
    }

    checkExistingSession()

    return () => {
      cancelled = true
    }
  }, [navigate, setEntityContext, setUserContext])

  useEffect(() => {
    if (!controlModalOpen || timerSeconds <= 0) return
    const t = setInterval(() => {
      setTimerSeconds((prev) => (prev <= 1 ? 0 : prev - 1))
    }, 1000)
    return () => clearInterval(t)
  }, [controlModalOpen, timerSeconds])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setState({ ...state, [name]: value })
  }

  const requestCode = useCallback(async () => {
    if (!state.email.trim()) return
    setControlLoading(true)
    try {
      const userService = new UserService()
      await userService.requestControlCode(state.email.trim())
      setControlModalOpen(true)
      setTimerSeconds(CODE_VALIDITY_SECONDS)
      setControlCode('')
      toast.success('Código enviado. Revisa la casilla de control.')
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        const data = error.response.data as { message?: string; blockedUntil?: number }
        toast.error(data.message ?? 'Error al solicitar el código')
        if (data.blockedUntil != null) {
          const until = new Date(data.blockedUntil)
          toast.error(`Bloqueado hasta ${until.toLocaleTimeString()}. Intente en 2 horas.`)
        }
      }
    } finally {
      setControlLoading(false)
    }
  }, [state.email])

  const onRequestNewCode = () => {
    requestCode()
  }

  const onVerifyCode = async () => {
    if (!controlCode.trim()) {
      toast.error('Ingrese el código')
      return
    }
    setControlLoading(true)
    try {
      const userService = new UserService()
      const data = await userService.verifyControlCode(state.email.trim(), controlCode.trim().toUpperCase())
      if (data) {
        setUserContext(data.user)
        setEntityContext(data.entity)
      }
      setControlModalOpen(false)
      navigate(`/${urls.app.index}/${urls.app.main.electronicBill.form}`)
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        const data = error.response.data as { message?: string; blockedUntil?: number }
        toast.error(data.message ?? 'Error al verificar')
        if (data.blockedUntil != null) {
          toast.error(texts.state.modal.blockedMessage)
        }
      }
    } finally {
      setControlLoading(false)
    }
  }

  const handleForgotPasswordClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    setForgotPasswordOpen(true)
  }

  async function onLogin (e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (isControlUserFlow) {
      await requestCode()
      return
    }
    try {
      const userService = new UserService()
      const data = await userService.login(state.email, state.password)
      if (data) {
        setUserContext(data.user)
        setEntityContext(data.entity)
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const serverError = error as AxiosError<ServerError>
        if (serverError?.response?.data) {
          return toast.error(serverError.response.data.message)
        }
      }
    }
    navigate(`/${urls.app.index}/${urls.app.main.electronicBill.form}`)
  }

  if (restoringSession) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <div className={classes.paper}>

        <Avatar className={classes.avatar}>
          <img
            src={texts.header.logo.path}
            alt={texts.header.logo.alt}
          />
        </Avatar>

        <Typography component="h1" variant="h5">
          {texts.header.title}
        </Typography>

        <form className={classes.state} onSubmit={onLogin} noValidate>

          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            onChange={handleChange}
            value={state.email}
            id={texts.state.field.email.name}
            label={texts.state.field.email.label}
            name={texts.state.field.email.name}
            autoFocus
          />

          {!isControlUserFlow && (
            <>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                value={state.password}
                onChange={handleChange}
                name={texts.state.field.password.name}
                label={texts.state.field.password.label}
                type="password"
                id={texts.state.field.password.name}
                autoComplete="current-password"
              />
              <FormControlLabel
                control={<Checkbox value={texts.state.field.rememberCheck.value} color="primary" />}
                label={texts.state.field.rememberCheck.label}
              />
            </>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            className={classes.submit}
            disabled={controlLoading}
          >
            {isControlUserFlow ? texts.state.field.controlSigninButton.title : texts.state.field.signinButton.title}
          </Button>

          <Grid container>
            <Grid item xs>
              <Link href="#" variant="body2" onClick={handleForgotPasswordClick}>
                {texts.state.footer.forgotPasswordLink}
              </Link>
            </Grid>
            <Grid item>
              <Link href="#" variant="body2">
                {texts.state.footer.createAccountLink}
              </Link>
            </Grid>
          </Grid>
        </form>
      </div>

      <ForgotPasswordDialog
        open={forgotPasswordOpen}
        initialEmail={state.email}
        onClose={() => setForgotPasswordOpen(false)}
      />

      <Dialog open={controlModalOpen} onClose={() => !controlLoading && setControlModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{texts.state.modal.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
            {texts.state.modal.codeSent}
          </Typography>
          <Typography className={classes.timer} variant="h6" color={timerSeconds === 0 ? ('error' as const) : ('textPrimary' as const)}>
            {timerSeconds > 0 ? `Vence en ${formatTimer(timerSeconds)}` : 'Código vencido'}
          </Typography>
          <TextField
            variant="outlined"
            margin="normal"
            fullWidth
            value={controlCode}
            onChange={(e) => setControlCode(e.target.value.toUpperCase().slice(0, 6))}
            label={texts.state.field.controlCode.label}
            name={texts.state.field.controlCode.name}
            inputProps={{ maxLength: 6, style: { letterSpacing: 4, fontSize: '1.2rem' } }}
            disabled={controlLoading}
          />
        </DialogContent>
        <DialogActions>
          {timerSeconds === 0 ? (
            <Button onClick={onRequestNewCode} color="primary" disabled={controlLoading}>
              {texts.state.modal.requestNewCode}
            </Button>
          ) : null}
          <Button onClick={onVerifyCode} color="primary" variant="contained" disabled={controlLoading}>
            {texts.state.modal.verify}
          </Button>
        </DialogActions>
      </Dialog>

      <Box mt={8}>
        <Copyright />
      </Box>
    </Container>
  )
}
